import { CalendarDays } from "lucide-react";
import {
  useGetList,
  useLocaleState,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import { startOfDay } from "../events/calendarUtils";
import { EventEditDialog } from "../events/EventDialogs";
import { useEventTypeLookup } from "../events/eventTypes";
import { formatEventDate } from "../events/formatEvent";
import type { CrmEvent } from "../types";

const UPCOMING_EVENTS_SHOWN = 5;

/** The next events at the venue; a click opens the event. */
export const UpcomingEvents = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const lookupType = useEventTypeLookup();
  const [selectedId, setSelectedId] = useState<Identifier | null>(null);
  // Fixed for the page's lifetime, so the query key stays stable.
  const filter = useMemo(
    () => ({ "start_at@gte": startOfDay(new Date()).toISOString() }),
    [],
  );
  const { data: events = [], isPending } = useGetList<CrmEvent>("events", {
    filter,
    pagination: { page: 1, perPage: UPCOMING_EVENTS_SHOWN },
    sort: { field: "start_at", order: "ASC" },
  });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <CalendarDays className="text-muted-foreground size-5 shrink-0 mr-3" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex-1">
          {translate("crm.dashboard.upcoming_events")}
        </h2>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/events">{translate("crm.dashboard.see_all")}</Link>
        </Button>
      </div>
      <Card className="py-1 gap-0">
        {!isPending && events.length === 0 && (
          <p className="px-4 py-3 text-sm text-muted-foreground">
            {translate("crm.dashboard.no_upcoming_events")}
          </p>
        )}
        {events.map((event) => {
          const type = lookupType(event.type);
          return (
            <button
              key={event.id}
              type="button"
              onClick={() => setSelectedId(event.id)}
              className="flex gap-3 items-start text-left px-4 py-2 hover:bg-muted/60 cursor-pointer"
            >
              <span
                className="mt-1.5 size-2.5 rounded-full shrink-0"
                style={{ backgroundColor: type.color }}
              />
              <span className="flex flex-col min-w-0">
                <span className="text-sm font-medium truncate">
                  {event.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatEventDate(event, locale)}
                  {type.label ? ` · ${type.label}` : ""}
                </span>
              </span>
            </button>
          );
        })}
      </Card>
      <EventEditDialog
        eventId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
};
